import { STUDENT_ID_TYPES } from '../constants/bluebox';

const FALLBACK_STUDENT_ID = '-'; // fallback value
export const FALLBACK_STUDENT_ID_ROSTER = 'N/A'; // fallback value for roster UI
/**
 * Utility function to generate a student ID based on a provided list of student IDs
 * and a specific student ID type. If no matching ID is found, a fallback value ('N/A') is returned.
 *
 * @param studentIds - Array of objects containing `studentIdTypeCd` and `studentId`.
 * @param studentIdType - (Optional) The type of student ID to search for.
 * @returns The matching student ID or a fallback value if no match is found.
 *
 * @example
 * const studentIds = [{ studentIdTypeCd: '1', studentId: '12345' }];
 * generateStudentId(studentIds, '1'); // Returns '12345'
 */

type StudentId = {
  studentIdTypeCd: string;
  studentId: string;
};

// Define a type for the array of student IDs
type StudentIdList = Array<StudentId>;

export function generateStudentId(
  studentIds: StudentIdList = [],
  studentIdType = '',
  fallback: string = FALLBACK_STUDENT_ID
): string {
  const student = (studentIds || []).find((student) => student.studentIdTypeCd === studentIdType);
  return student?.studentId ?? fallback;
}

/**
 * Utility functions to generate specific types of student IDs (school, district, state)
 * from a provided list of student IDs. If no matching ID is found, a fallback value ('N/A') is returned.
 */
export function generateSchoolId(studentIds: StudentIdList = [], fallback = FALLBACK_STUDENT_ID): string {
  return generateStudentId(studentIds, STUDENT_ID_TYPES.SCHOOL, fallback);
}

export function generateDistrictId(studentIds: StudentIdList = [], fallback = FALLBACK_STUDENT_ID): string {
  return generateStudentId(studentIds, STUDENT_ID_TYPES.DISTRICT, fallback);
}

export function generateStateId(studentIds: StudentIdList = [], fallback = FALLBACK_STUDENT_ID): string {
  return generateStudentId(studentIds, STUDENT_ID_TYPES.STATE, fallback);
}
